/**
 * Fill-in-the-blank questions a new knowledge bank usually needs. Each answer is
 * read to callers word for word once saved, so the blanks are in [brackets] and
 * the add form refuses to save while any remain.
 *
 * Keep answers short and spoken. Never include "goodbye" or "hang up" (either
 * ends the call), a price, or a claim about which insurance is accepted: those
 * are the assistant's hard rules, so the templates steer callers to the office.
 */
export interface StarterQuestion {
  id: string;
  category: string;
  question: string;
  answer: string;
}

export const STARTER_QUESTIONS: StarterQuestion[] = [
  // Hours and location
  {
    id: "hours",
    category: "Hours and location",
    question: "What are your hours?",
    answer:
      "We're open [Monday through Friday, 8 AM to 5 PM]. We're closed on [weekends and major holidays].",
  },
  {
    id: "weekends",
    category: "Hours and location",
    question: "Are you open on weekends or holidays?",
    answer:
      "[We're open Saturdays from 9 AM to 1 PM.] We're closed [on Sundays and major holidays].",
  },
  {
    id: "address",
    category: "Hours and location",
    question: "Where are you located?",
    answer:
      "We're at [123 Main Street, Suite 200, in Walnut Creek], [next to the public library].",
  },
  {
    id: "parking",
    category: "Hours and location",
    question: "Where do I park?",
    answer:
      "There's [free parking in the lot behind the building], and [metered street parking on Main Street]. The entrance is [on the Main Street side].",
  },
  {
    id: "accessibility",
    category: "Hours and location",
    question: "Is the office wheelchair accessible?",
    answer:
      "Yes. [The building has a ramp at the front entrance and an elevator to our floor.]",
  },

  // Appointments
  {
    id: "scheduling",
    category: "Appointments",
    question: "How do I schedule an appointment?",
    answer:
      "You can [book through our website, call the office during business hours, or use the patient portal]. [New patients should allow about an hour for a first visit.]",
  },
  {
    id: "first-visit-length",
    category: "Appointments",
    question: "How long does a first visit take?",
    answer:
      "First visits usually take about [60 minutes]. Follow-up visits are about [20 to 30 minutes].",
  },
  {
    id: "what-to-bring",
    category: "Appointments",
    question: "What should I bring to my appointment?",
    answer:
      "Please bring [a photo ID, your insurance card, a list of your current medications, and any referral paperwork]. [New patients should arrive 15 minutes early to complete forms.]",
  },
  {
    id: "cancellation",
    category: "Appointments",
    question: "What is your cancellation policy?",
    answer:
      "Please let us know at least [24 hours] before your appointment if you need to cancel or reschedule. [Late cancellations and missed visits may be subject to a fee.]",
  },
  {
    id: "running-late",
    category: "Appointments",
    question: "What if I'm running late?",
    answer:
      "If you're more than [15 minutes] late, we may need to reschedule your visit. Please call the office to let us know you're on your way.",
  },
  {
    id: "telehealth",
    category: "Appointments",
    question: "Do you offer telehealth or video visits?",
    answer:
      "[Yes, we offer video visits for follow-ups and some new concerns.] [Your provider decides whether a video visit is a good fit.]",
  },

  // New patients
  {
    id: "accepting-new-patients",
    category: "New patients",
    question: "Are you accepting new patients?",
    answer:
      "[Yes, we're accepting new patients.] [The wait for a first visit is currently about two weeks.]",
  },
  {
    id: "become-new-patient",
    category: "New patients",
    question: "How do I become a new patient?",
    answer:
      "[Fill out the new patient form on our website], then [call the office or book online]. We'll [send your intake paperwork through the patient portal before your visit].",
  },
  {
    id: "ages",
    category: "New patients",
    question: "What ages do you see?",
    answer: "We see patients [from newborns through age 21].",
  },

  // Services and providers
  {
    id: "services",
    category: "Services and providers",
    question: "What services do you offer?",
    answer:
      "We offer [primary care, annual physicals, vaccinations, and same-day sick visits]. We don't offer [X-rays or lab draws on site].",
  },
  {
    id: "providers",
    category: "Services and providers",
    question: "Who are your providers?",
    answer:
      "Our providers are [Dr. Jane Smith, Dr. Ramon Ortiz, and nurse practitioner Kelly Chen].",
  },
  {
    id: "languages",
    category: "Services and providers",
    question: "Do you have staff who speak languages other than English?",
    answer:
      "[Spanish-speaking staff are available], and we can arrange an interpreter for other languages [with advance notice].",
  },

  // Prescriptions, records, results
  {
    id: "refills",
    category: "Prescriptions, records, and results",
    question: "How do I get a prescription refill?",
    answer:
      "Ask your pharmacy to send the refill request to us, or [request it through the patient portal]. Refills take [up to two business days]. [Controlled medications require an appointment.]",
  },
  {
    id: "records",
    category: "Prescriptions, records, and results",
    question: "How do I get a copy of my medical records?",
    answer:
      "Records requests go through [the patient portal or the signed release form on our website]. Please allow [up to 30 days] for processing.",
  },
  {
    id: "results",
    category: "Prescriptions, records, and results",
    question: "How do I get my test results?",
    answer:
      "Results are posted to [the patient portal] once your provider has reviewed them, usually within [three to five business days]. [The office will call you about anything that needs prompt attention.]",
  },
  {
    id: "forms-referrals",
    category: "Prescriptions, records, and results",
    question: "How do I get a form filled out or a referral?",
    answer:
      "Send the form to [our fax number or through the patient portal]. Forms and referrals take [up to five business days].",
  },

  // Insurance, billing, contact
  {
    id: "insurance",
    category: "Insurance, billing, and contact",
    question: "Do you take my insurance?",
    answer:
      "I'm not able to confirm insurance or coverage on this line. Please [call the office during business hours at (555) 123-4567] or [bring your card to your visit] and the team will check for you.",
  },
  {
    id: "billing",
    category: "Insurance, billing, and contact",
    question: "How do I pay a bill or ask a billing question?",
    answer:
      "You can pay [online through the patient portal] or reach our billing team at [(555) 123-4567, Monday through Friday, 9 AM to 4 PM].",
  },
  {
    id: "fax-email",
    category: "Insurance, billing, and contact",
    question: "What is your fax number or email address?",
    answer:
      "Our fax number is [(555) 123-4568], and the front desk email is [frontdesk@example.com].",
  },
  {
    id: "after-hours",
    category: "Insurance, billing, and contact",
    question: "What should I do if I need help after hours?",
    answer:
      "For urgent but non-emergency concerns after hours, [call our main number and follow the prompts to reach the on-call provider]. If this is a medical emergency, please dial nine-one-one.",
  },
];

// Empty brackets count too: "[]" is what a half-finished edit looks like.
const PLACEHOLDER_PATTERN = /\[[^\]]*\]/;

/** True while a [bracketed] blank, filled or not, is still in the text. */
export function hasUnfilledPlaceholder(text: string): boolean {
  return PLACEHOLDER_PATTERN.test(text);
}

const normalize = (text: string): string => text.trim().toLowerCase().replace(/[?.!]+$/, "");

/** Starters whose question this group has not already answered. */
export function uncoveredStarters(existingQuestions: string[]): StarterQuestion[] {
  const covered = new Set(existingQuestions.map(normalize));
  return STARTER_QUESTIONS.filter((starter) => !covered.has(normalize(starter.question)));
}
